import React, { useEffect, useRef, useMemo } from 'react';

/**
 * PokemonTypeCanvas
 * -----------------------------------------------------------
 * Dense, painterly particle systems per Pokémon type. Runs on
 * requestAnimationFrame with a single canvas per type-region.
 *
 * Types covered: fire, water, grass, electric, ice, rock,
 * flying, poison, psychic, ghost, fighting, dragon, dark,
 * steel, fairy, ground, bug, normal.
 *
 * Props:
 *   types  string[]  one or two type keys
 */

const TAU = Math.PI * 2;

// ─── helpers ─────────────────────────────────────────────────────────
const rnd  = (min, max) => Math.random() * (max - min) + min;
const rndi = (min, max) => Math.floor(rnd(min, max));
const seed = (i, s) => {
  const x = Math.sin(i * 999.31 + s * 71.17) * 10000;
  return x - Math.floor(x);
};

// ─── biome tints (radial washes, drawn each frame) ───────────────────
const TINTS = {
  fire:     [{ x: 0.2,  y: 0.15, r: 1.2, color: 'rgba(255,165,90,0.32)' }, { x: 0.9, y: 0.9, r: 1.2, color: 'rgba(238,129,48,0.26)' }],
  water:    [{ x: 0.5,  y: 0.9,  r: 1.5, color: 'rgba(60,110,200,0.34)' }, { x: 0.5, y: 0.1, r: 1.2, color: 'rgba(120,180,255,0.24)' }],
  electric: [{ x: 0.3,  y: 0.1,  r: 1.2, color: 'rgba(247,208,44,0.32)' }, { x: 0.9, y: 0.9, r: 1.2, color: 'rgba(240,190,30,0.26)' }],
  grass:    [{ x: 0.5,  y: 0.9,  r: 1.5, color: 'rgba(80,150,60,0.30)'  }, { x: 0.5, y: 0.1, r: 1.2, color: 'rgba(180,230,150,0.26)' }],
  ice:      [{ x: 0.5,  y: 0.5,  r: 1.4, color: 'rgba(200,240,240,0.34)' }, { x: 0.5, y: 0.9, r: 1.2, color: 'rgba(140,200,220,0.26)' }],
  flying:   [{ x: 0.5,  y: 0.2,  r: 1.4, color: 'rgba(200,220,255,0.36)' }, { x: 0.5, y: 0.9, r: 1.2, color: 'rgba(150,180,240,0.20)' }],
  psychic:  [{ x: 0.3,  y: 0.2,  r: 1.4, color: 'rgba(249,85,135,0.30)'  }, { x: 0.8, y: 0.9, r: 1.4, color: 'rgba(180,60,160,0.26)' }],
  ghost:    [{ x: 0.5,  y: 0.5,  r: 1.5, color: 'rgba(80,60,110,0.36)'   }, { x: 0.5, y: 0.9, r: 1.2, color: 'rgba(40,30,60,0.26)'   }],
  dragon:   [{ x: 0.25, y: 0.15, r: 1.4, color: 'rgba(111,53,252,0.30)'  }, { x: 0.9, y: 0.9, r: 1.4, color: 'rgba(90,40,200,0.26)'  }],
  dark:     [{ x: 0.5,  y: 0.5,  r: 1.5, color: 'rgba(60,50,45,0.38)'    }, { x: 0.5, y: 0.9, r: 1.2, color: 'rgba(30,25,25,0.30)'   }],
  steel:    [{ x: 0.5,  y: 0.5,  r: 1.5, color: 'rgba(160,160,195,0.30)' }, { x: 0.8, y: 0.9, r: 1.2, color: 'rgba(200,200,220,0.20)' }],
  fairy:    [{ x: 0.3,  y: 0.2,  r: 1.4, color: 'rgba(214,133,173,0.32)' }, { x: 0.9, y: 0.9, r: 1.4, color: 'rgba(180,100,150,0.26)' }],
  fighting: [{ x: 0.5,  y: 0.5,  r: 1.4, color: 'rgba(194,46,40,0.32)'   }, { x: 0.8, y: 0.9, r: 1.2, color: 'rgba(140,30,30,0.26)'  }],
  poison:   [{ x: 0.5,  y: 0.8,  r: 1.4, color: 'rgba(130,50,140,0.34)'  }, { x: 0.5, y: 0.1, r: 1.2, color: 'rgba(180,80,180,0.26)' }],
  ground:   [{ x: 0.5,  y: 0.9,  r: 1.5, color: 'rgba(180,140,60,0.32)'  }, { x: 0.5, y: 0.1, r: 1.2, color: 'rgba(226,191,101,0.28)' }],
  rock:     [{ x: 0.5,  y: 0.9,  r: 1.5, color: 'rgba(55,40,30,0.55)'    }, { x: 0.5, y: 0.1, r: 1.2, color: 'rgba(95,75,55,0.40)'  }],
  bug:      [{ x: 0.3,  y: 0.2,  r: 1.4, color: 'rgba(166,185,26,0.30)'  }, { x: 0.9, y: 0.9, r: 1.4, color: 'rgba(120,140,20,0.24)' }],
  normal:   [{ x: 0.3,  y: 0.2,  r: 1.4, color: 'rgba(168,167,122,0.26)' }, { x: 0.9, y: 0.9, r: 1.4, color: 'rgba(140,140,100,0.20)' }],
};

const drawTint = (ctx, w, h, type) => {
  const tints = TINTS[type];
  if (!tints) return;
  for (const t of tints) {
    const g = ctx.createRadialGradient(
      t.x * w, t.y * h, 0,
      t.x * w, t.y * h, t.r * Math.max(w, h)
    );
    g.addColorStop(0, t.color);
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, w, h);
  }
};

// Rock: darker cross-hatch cave texture with jagged rims
const drawRockTexture = (ctx, w, h) => {
  ctx.save();
  ctx.globalAlpha = 0.14;
  ctx.strokeStyle = '#2a1c14';
  ctx.lineWidth = 1;
  for (let y = -h; y < h * 2; y += 10) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y + w); ctx.stroke();
  }
  for (let y = -h; y < h * 2; y += 14) {
    ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y - w); ctx.stroke();
  }
  ctx.globalAlpha = 0.22;
  ctx.fillStyle = '#1a110a';
  ctx.beginPath();
  ctx.moveTo(0, h * 0.85);
  for (let x = 0; x <= w; x += w / 8) {
    ctx.lineTo(x, h * 0.85 + Math.sin(x * 0.05) * 6 - 4);
  }
  ctx.lineTo(w, h); ctx.lineTo(0, h); ctx.closePath(); ctx.fill();
  ctx.restore();
};

// ─── particle factories ──────────────────────────────────────────────
const FACTORIES = {
  // FIRE — mix of large embers & small sparks, wobbly rise
  fire: (i, w, h) => {
    const isBig = i % 3 === 0;
    return {
      x: seed(i, 1) * w,
      y: h + rnd(0, 30),
      vx: rnd(-0.3, 0.3),
      vy: -rnd(0.6, 1.6),
      r: isBig ? rnd(3.2, 5.5) : rnd(1.4, 2.6),
      isBig,
      life: 0,
      ttl: rnd(90, 170),
      hue: rnd(14, 52),
      wobble: rnd(0, TAU),
    };
  },
  // WATER — small & large bubbles rising with lateral sway
  water: (i, w, h) => {
    const isBig = i % 4 === 0;
    return {
      x: seed(i, 2) * w,
      y: h + rnd(0, 40),
      vy: -rnd(0.4, isBig ? 0.9 : 1.1),
      r: isBig ? rnd(5, 8) : rnd(1.6, 3.6),
      phase: seed(i, 3) * TAU,
      swayAmp: isBig ? rnd(5, 9) : rnd(3, 6),
      life: 0,
      ttl: rnd(180, 320),
    };
  },
  // GRASS — three leaf shapes falling with rotation
  grass: (i, w, h) => ({
    x: seed(i, 4) * w,
    y: -rnd(0, 40),
    vy: rnd(0.4, 0.85),
    vx: rnd(-0.3, 0.3),
    angle: rnd(0, TAU),
    spin: rnd(-0.05, 0.05),
    shape: i % 3,                  // 0 = ellipse, 1 = teardrop, 2 = ribbon
    r: rnd(4, 8),
    hue: rndi(90, 145),
    life: 0,
    ttl: rnd(280, 440),
    sway: rnd(0.5, 1.2),
  }),
  // ELECTRIC — branched bolts + micro sparks
  electric: (i, w, h) => {
    if (i < 4) {
      // main bolt
      return {
        kind: 'bolt',
        x: rnd(w * 0.15, w * 0.85),
        yTop: rnd(-8, h * 0.35),
        height: rnd(h * 0.5, h * 0.95),
        nextFlashIn: rndi(10, 90),
        onFrames: 0,
        life: 0,
        ttl: 99999,
        zag: [],
        branches: [],
      };
    }
    // spark
    return {
      kind: 'spark',
      x: rnd(0, w),
      y: rnd(0, h),
      vx: rnd(-1.4, 1.4),
      vy: rnd(-1.4, 1.4),
      r: rnd(0.8, 2),
      life: 0,
      ttl: rndi(20, 55),
    };
  },
  // ROCK — polygonal fragments + dust
  rock: (i, w, h) => {
    const isFrag = i % 3 === 0;
    return {
      kind: isFrag ? 'frag' : 'dust',
      x: seed(i, 5) * w,
      y: -rnd(0, 40),
      vy: isFrag ? rnd(0.6, 1.2) : rnd(0.3, 0.6),
      vx: rnd(-0.15, 0.15),
      angle: rnd(0, TAU),
      spin: rnd(-0.06, 0.06),
      r: isFrag ? rnd(3, 6) : rnd(0.6, 1.6),
      sides: rndi(4, 7),
      life: 0,
      ttl: rnd(220, 380),
    };
  },
  // ICE — big & small flakes with wide lateral drift
  ice: (i, w, h) => {
    const isBig = i % 3 === 0;
    return {
      x: seed(i, 6) * w,
      y: -rnd(0, 40),
      vy: rnd(0.3, isBig ? 0.6 : 0.9),
      vx: rnd(-0.25, 0.25),
      angle: rnd(0, TAU),
      spin: rnd(-0.04, 0.04),
      r: isBig ? rnd(5, 8) : rnd(1.8, 3.4),
      arms: isBig ? 6 : 4,
      drift: rnd(0.8, 1.6),
      life: 0,
      ttl: rnd(280, 440),
    };
  },
  // FLYING — voluminous stacked clouds
  flying: (i, w, h) => ({
    x: -rnd(0, w * 0.5) + seed(i, 7) * w,
    y: rnd(h * 0.1, h * 0.75),
    vx: rnd(0.18, 0.42),
    w: rnd(70, 130),
    h: rnd(26, 44),
    puffs: rndi(4, 7),
    life: 0,
    ttl: rnd(500, 900),
  }),
  // POISON — bubbles cling to edges & slide down
  poison: (i, w, h) => {
    const side = i % 2 === 0 ? 'L' : 'R';
    const edgeX = side === 'L' ? rnd(0, w * 0.18) : rnd(w * 0.82, w);
    return {
      x: edgeX,
      y: -rnd(0, 40),
      vy: rnd(0.35, 0.9),
      phase: rnd(0, TAU),
      r: rnd(3, 6),
      side,
      pop: 0,               // 0 = normal, >0 = popping frames
      life: 0,
      ttl: rnd(220, 360),
    };
  },
  // PSYCHIC — spiraling orbs (radius modulates with angle)
  psychic: (i, w, h) => ({
    cx: w * 0.5,
    cy: h * 0.55,
    baseRadius: rnd(w * 0.10, w * 0.30),
    spiralAmp: rnd(w * 0.08, w * 0.18),
    angle: rnd(0, TAU),
    speed: rnd(0.010, 0.024) * (i % 2 === 0 ? 1 : -1),
    r: rnd(2.4, 4.2),
    hue: rnd(280, 350),
    life: 0,
    ttl: 99999,
  }),
  // GHOST — mist blobs moving in any direction
  ghost: (i, w, h) => ({
    x: seed(i, 9) * w,
    y: rnd(h * 0.1, h * 0.9),
    vx: rnd(-0.35, 0.35),
    vy: rnd(-0.25, 0.25),
    w: rnd(50, 110),
    h: rnd(24, 40),
    life: 0,
    ttl: rnd(320, 540),
  }),
  // FIGHTING — expanding shockwave rings + radial sparks
  fighting: (i, w, h) => {
    if (i % 6 === 0) {
      return {
        kind: 'ring',
        cx: w * 0.5,
        cy: h * 0.5,
        r: 0,
        growth: rnd(1.4, 2.4),
        life: 0,
        ttl: rndi(45, 75),
      };
    }
    return {
      kind: 'spark',
      cx: w * 0.5,
      cy: h * 0.5,
      x: w * 0.5,
      y: h * 0.5,
      angle: rnd(0, TAU),
      speed: rnd(1.8, 3.6),
      r: rnd(1.4, 2.6),
      life: 0,
      ttl: rndi(45, 90),
    };
  },
  // DRAGON — glowing runes orbiting
  dragon: (i, w, h) => ({
    cx: w * 0.5,
    cy: h * 0.55,
    radius: rnd(w * 0.16, w * 0.42),
    angle: rnd(0, TAU),
    speed: rnd(0.008, 0.018) * (i % 2 === 0 ? -1 : 1),
    r: rnd(2.4, 3.8),
    life: 0,
    ttl: 99999,
  }),
  // DARK — dark wisps
  dark: (i, w, h) => ({
    x: seed(i, 10) * w,
    y: rnd(h * 0.15, h * 0.85),
    vx: rnd(-0.2, 0.3),
    vy: rnd(-0.15, 0.15),
    w: rnd(55, 100),
    h: rnd(20, 32),
    life: 0,
    ttl: rnd(360, 560),
  }),
  // STEEL — diagonal shimmer sweep
  steel: (i, w, h) => ({
    x: -w * 0.4 + seed(i, 20) * w * 0.5,
    vx: rnd(2.0, 3.4),
    width: w * rnd(0.22, 0.38),
    life: 0,
    ttl: 99999,
  }),
  // FAIRY — twinkling sparkles
  fairy: (i, w, h) => ({
    x: seed(i, 11) * w,
    y: seed(i, 12) * h,
    r: rnd(1.8, 3.6),
    life: 0,
    ttl: rnd(90, 170),
    phase: rnd(0, TAU),
  }),
  // GROUND — sand grains swirl
  ground: (i, w, h) => ({
    x: seed(i, 13) * w,
    y: rnd(h * 0.15, h * 0.95),
    vx: rnd(0.4, 1.0),
    vyOsc: rnd(0.5, 1.0),
    phase: rnd(0, TAU),
    r: rnd(1, 2.2),
    life: 0,
    ttl: rnd(200, 320),
  }),
  // BUG — firefly flutter
  bug: (i, w, h) => ({
    x: seed(i, 14) * w,
    y: seed(i, 15) * h,
    vx: rnd(-0.4, 0.4),
    vy: rnd(-0.4, 0.4),
    r: rnd(1.8, 3),
    life: 0,
    ttl: rnd(140, 260),
    twinkle: rnd(0, TAU),
  }),
  // NORMAL — soft dots
  normal: (i, w, h) => ({
    x: seed(i, 16) * w,
    y: seed(i, 17) * h,
    r: rnd(1.4, 2.6),
    life: 0,
    ttl: rnd(120, 220),
    phase: rnd(0, TAU),
  }),
};

// ─── particle counts (density) ───────────────────────────────────────
const COUNT = {
  fire: 75, water: 55, grass: 45, electric: 22, rock: 55, ice: 55,
  flying: 8, poison: 40, psychic: 32, ghost: 10, fighting: 40,
  dragon: 24, dark: 8, steel: 3, fairy: 45, ground: 32, bug: 26, normal: 24,
};

// ─── per-type update + draw ──────────────────────────────────────────
const STEP = {
  fire: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y < -20) return false;
    p.wobble += 0.15;
    p.x += p.vx + Math.sin(p.wobble) * 0.5;
    p.y += p.vy;
    p.vx += rnd(-0.06, 0.06);
    const t = p.life / p.ttl;
    const alpha = t < 0.12 ? t / 0.12 : Math.max(0, 1 - t);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    // outer glow
    const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * (p.isBig ? 4 : 3));
    g.addColorStop(0, `hsla(${p.hue},100%,80%,${0.95 * alpha})`);
    g.addColorStop(0.4, `hsla(${p.hue},100%,60%,${0.8 * alpha})`);
    g.addColorStop(1, `hsla(${p.hue},100%,40%,0)`);
    ctx.fillStyle = g;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * (p.isBig ? 4 : 3), 0, TAU); ctx.fill();
    if (p.isBig) {
      // hot core
      ctx.fillStyle = `hsla(50,100%,90%,${alpha})`;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 0.6, 0, TAU); ctx.fill();
    }
    ctx.restore();
    return true;
  },

  water: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y < -20) return false;
    p.y += p.vy;
    p.phase += 0.09;
    const dx = Math.sin(p.phase) * p.swayAmp;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.85, p.life / 20) * Math.max(0, 1 - (t - 0.7) / 0.3);
    ctx.save();
    ctx.globalAlpha = alpha;
    const grad = ctx.createRadialGradient(p.x + dx - p.r * 0.3, p.y - p.r * 0.3, 0, p.x + dx, p.y, p.r);
    grad.addColorStop(0, 'rgba(255,255,255,0.95)');
    grad.addColorStop(0.55, 'rgba(160,220,255,0.6)');
    grad.addColorStop(1, 'rgba(60,150,240,0.7)');
    ctx.fillStyle = grad;
    ctx.beginPath(); ctx.arc(p.x + dx, p.y, p.r, 0, TAU); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 0.7; ctx.stroke();
    // highlight dot
    ctx.fillStyle = 'rgba(255,255,255,0.9)';
    ctx.beginPath();
    ctx.arc(p.x + dx - p.r * 0.35, p.y - p.r * 0.4, p.r * 0.18, 0, TAU);
    ctx.fill();
    ctx.restore();
    return true;
  },

  grass: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y > h + 20) return false;
    p.y += p.vy;
    p.x += Math.sin(p.life * 0.04) * p.sway + p.vx;
    p.angle += p.spin;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.9, p.life / 15) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.globalAlpha = alpha;
    const grd = ctx.createLinearGradient(-p.r, -p.r, p.r, p.r);
    grd.addColorStop(0, `hsl(${p.hue},70%,68%)`);
    grd.addColorStop(1, `hsl(${p.hue},80%,32%)`);
    ctx.fillStyle = grd;
    ctx.strokeStyle = `hsl(${p.hue},80%,25%)`;
    ctx.lineWidth = 0.8;
    ctx.beginPath();
    if (p.shape === 0) {
      // ellipse leaf
      ctx.ellipse(0, 0, p.r * 0.75, p.r * 1.4, 0, 0, TAU);
    } else if (p.shape === 1) {
      // teardrop
      ctx.moveTo(0, -p.r * 1.3);
      ctx.quadraticCurveTo(p.r, 0, 0, p.r * 1.3);
      ctx.quadraticCurveTo(-p.r, 0, 0, -p.r * 1.3);
    } else {
      // ribbon leaf
      ctx.moveTo(-p.r * 0.4, -p.r * 1.4);
      ctx.bezierCurveTo(p.r, -p.r, p.r * 0.6, p.r, 0, p.r * 1.5);
      ctx.bezierCurveTo(-p.r, p.r * 0.4, -p.r * 0.9, -p.r * 0.5, -p.r * 0.4, -p.r * 1.4);
    }
    ctx.fill(); ctx.stroke();
    // vein
    ctx.strokeStyle = `hsla(${p.hue},70%,90%,0.6)`;
    ctx.lineWidth = 0.6;
    ctx.beginPath(); ctx.moveTo(0, -p.r * 1.2); ctx.lineTo(0, p.r * 1.2); ctx.stroke();
    ctx.restore();
    return true;
  },

  electric: (p, ctx, w, h) => {
    p.life++;
    if (p.kind === 'spark') {
      if (p.life > p.ttl) return false;
      p.x += p.vx; p.y += p.vy;
      const t = p.life / p.ttl;
      const alpha = Math.max(0, 1 - t);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowColor = 'rgba(255,240,120,1)';
      ctx.shadowBlur = 10;
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3);
      g.addColorStop(0, `rgba(255,255,220,${alpha})`);
      g.addColorStop(1, 'rgba(255,220,60,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3, 0, TAU); ctx.fill();
      ctx.restore();
      return true;
    }
    // bolt
    p.nextFlashIn--;
    if (p.nextFlashIn <= 0 && p.onFrames <= 0) {
      p.onFrames = rndi(4, 9);
      const steps = 10;
      p.zag = [];
      let x = p.x;
      const yStart = p.yTop;
      for (let s = 0; s <= steps; s++) {
        const y = yStart + (p.height * s) / steps;
        x += rnd(-10, 10);
        p.zag.push([x, y]);
      }
      // branches
      p.branches = [];
      const nBranch = rndi(1, 4);
      for (let b = 0; b < nBranch; b++) {
        const from = p.zag[rndi(2, p.zag.length - 2)];
        const branch = [from];
        let bx = from[0], by = from[1];
        const dir = Math.random() < 0.5 ? -1 : 1;
        const len = rndi(3, 6);
        for (let s = 0; s < len; s++) {
          bx += dir * rnd(4, 12);
          by += rnd(4, 14);
          branch.push([bx, by]);
        }
        p.branches.push(branch);
      }
      p.nextFlashIn = rndi(20, 90);
    }
    if (p.onFrames > 0) {
      p.onFrames--;
      const glow = p.onFrames / 9;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowColor = 'rgba(255,220,60,1)';
      ctx.shadowBlur = 18;
      ctx.strokeStyle = `rgba(255,250,180,${0.85 + 0.15 * glow})`;
      ctx.lineWidth = 2.6;
      ctx.beginPath();
      ctx.moveTo(p.zag[0][0], p.zag[0][1]);
      for (let s = 1; s < p.zag.length; s++) ctx.lineTo(p.zag[s][0], p.zag[s][1]);
      ctx.stroke();
      // outer halo
      ctx.strokeStyle = `rgba(255,240,150,${0.4 * glow})`;
      ctx.lineWidth = 6;
      ctx.stroke();
      // branches
      ctx.lineWidth = 1.6;
      ctx.strokeStyle = `rgba(255,235,130,${0.85})`;
      for (const br of p.branches) {
        ctx.beginPath();
        ctx.moveTo(br[0][0], br[0][1]);
        for (let s = 1; s < br.length; s++) ctx.lineTo(br[s][0], br[s][1]);
        ctx.stroke();
      }
      ctx.restore();
    }
    return true;
  },

  rock: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y > h + 20) return false;
    p.y += p.vy; p.x += p.vx; p.angle += p.spin;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.85, p.life / 15) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.globalAlpha = alpha;
    if (p.kind === 'dust') {
      ctx.fillStyle = '#c9a97a';
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, TAU); ctx.fill();
    } else {
      ctx.translate(p.x, p.y);
      ctx.rotate(p.angle);
      const grd = ctx.createLinearGradient(-p.r, -p.r, p.r, p.r);
      grd.addColorStop(0, '#8a6b46');
      grd.addColorStop(1, '#3a2818');
      ctx.fillStyle = grd;
      ctx.strokeStyle = '#1a1006';
      ctx.lineWidth = 0.8;
      ctx.beginPath();
      for (let s = 0; s < p.sides; s++) {
        const a = (s / p.sides) * TAU;
        const rr = p.r * (0.75 + seed(s, p.sides) * 0.5);
        const x = Math.cos(a) * rr, y = Math.sin(a) * rr;
        if (s === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.fill(); ctx.stroke();
    }
    ctx.restore();
    return true;
  },

  ice: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y > h + 20) return false;
    p.y += p.vy;
    p.x += Math.sin(p.life * 0.03) * p.drift + p.vx;
    p.angle += p.spin;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.95, p.life / 15) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.translate(p.x, p.y);
    ctx.rotate(p.angle);
    ctx.globalAlpha = alpha;
    ctx.strokeStyle = 'rgba(240,250,255,0.98)';
    ctx.lineWidth = p.r > 4 ? 1.6 : 1.1;
    ctx.shadowColor = 'rgba(200,240,255,0.9)';
    ctx.shadowBlur = 10;
    for (let a = 0; a < p.arms; a++) {
      ctx.rotate(TAU / p.arms);
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(0, -p.r);
      // small branches
      ctx.moveTo(0, -p.r * 0.55);
      ctx.lineTo(-p.r * 0.25, -p.r * 0.75);
      ctx.moveTo(0, -p.r * 0.55);
      ctx.lineTo( p.r * 0.25, -p.r * 0.75);
      ctx.stroke();
    }
    ctx.restore();
    return true;
  },

  flying: (p, ctx, w, h) => {
    p.life++;
    if (p.x - p.w * 0.5 > w + 20) return false;
    p.x += p.vx;
    const alpha = Math.min(0.7, p.life / 60);
    ctx.save();
    ctx.globalAlpha = alpha;
    // multiple puff ellipses to look voluminous
    for (let n = 0; n < p.puffs; n++) {
      const s = seed(n, p.w);
      const ox = (n - p.puffs / 2) * p.w * 0.18 + (s - 0.5) * 8;
      const oy = (seed(n + 3, p.h) - 0.5) * p.h * 0.55;
      const rr = p.w * (0.42 + s * 0.18);
      const rh = p.h * (0.9 + seed(n + 5, p.w) * 0.35);
      const grd = ctx.createRadialGradient(p.x + ox - rr * 0.2, p.y + oy - rh * 0.2, 0, p.x + ox, p.y + oy, rr);
      grd.addColorStop(0, 'rgba(255,255,255,0.98)');
      grd.addColorStop(0.55, 'rgba(240,245,255,0.55)');
      grd.addColorStop(1, 'rgba(230,235,255,0)');
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.ellipse(p.x + ox, p.y + oy, rr * 0.55, rh, 0, 0, TAU);
      ctx.fill();
    }
    ctx.restore();
    return true;
  },

  poison: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.y > h + 20) return false;
    // if popping, only draw the pop and shrink
    if (p.pop > 0) {
      p.pop++;
      const t = p.pop / 12;
      if (t >= 1) return false;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.strokeStyle = `rgba(220,140,255,${1 - t})`;
      ctx.lineWidth = 1.4;
      const r = p.r * (1 + t * 2);
      ctx.beginPath(); ctx.arc(p.x, p.y, r, 0, TAU); ctx.stroke();
      // droplet splash lines
      for (let k = 0; k < 5; k++) {
        const a = (k / 5) * TAU;
        ctx.beginPath();
        ctx.moveTo(p.x + Math.cos(a) * p.r, p.y + Math.sin(a) * p.r);
        ctx.lineTo(p.x + Math.cos(a) * r, p.y + Math.sin(a) * r);
        ctx.stroke();
      }
      ctx.restore();
      return true;
    }
    p.y += p.vy;
    p.phase += 0.07;
    const dx = Math.sin(p.phase) * 3;
    // random pop chance near bottom
    if (p.y > h * 0.6 && Math.random() < 0.005) p.pop = 1;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.9, p.life / 18) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.shadowColor = 'rgba(200,120,230,0.9)';
    ctx.shadowBlur = 10;
    const cx = p.x + dx;
    const grd = ctx.createRadialGradient(cx - p.r * 0.3, p.y - p.r * 0.5, 0, cx, p.y, p.r * 1.3);
    grd.addColorStop(0, '#f0b8ff');
    grd.addColorStop(0.55, '#b048c4');
    grd.addColorStop(1, '#5a1560');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(cx, p.y, p.r, 0, TAU); ctx.fill();
    // highlight
    ctx.fillStyle = 'rgba(255,220,255,0.85)';
    ctx.beginPath();
    ctx.arc(cx - p.r * 0.3, p.y - p.r * 0.4, p.r * 0.22, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },

  psychic: (p, ctx, w, h) => {
    p.angle += p.speed;
    p.life++;
    const rad = p.baseRadius + Math.sin(p.life * 0.02) * p.spiralAmp;
    const x = p.cx + Math.cos(p.angle) * rad;
    const y = p.cy + Math.sin(p.angle) * rad * 0.7;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = `hsla(${p.hue},100%,70%,0.95)`;
    ctx.shadowBlur = 14;
    const grd = ctx.createRadialGradient(x, y, 0, x, y, p.r * 4);
    grd.addColorStop(0, `hsla(${p.hue},100%,92%,0.95)`);
    grd.addColorStop(0.5, `hsla(${p.hue},95%,65%,0.75)`);
    grd.addColorStop(1, `hsla(${p.hue},80%,45%,0)`);
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(x, y, p.r * 4, 0, TAU); ctx.fill();
    // inner core
    ctx.fillStyle = `hsla(${p.hue},100%,98%,0.95)`;
    ctx.beginPath(); ctx.arc(x, y, p.r * 0.7, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },

  ghost: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    p.x += p.vx; p.y += p.vy;
    p.vx += rnd(-0.02, 0.02); p.vy += rnd(-0.02, 0.02);
    // clamp velocities
    p.vx = Math.max(-0.5, Math.min(0.5, p.vx));
    p.vy = Math.max(-0.4, Math.min(0.4, p.vy));
    if (p.x < -50 || p.x > w + 50 || p.y < -40 || p.y > h + 40) return false;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.85, p.life / 40) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.filter = 'blur(3px)';
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.w * 0.6);
    grd.addColorStop(0, 'rgba(220,205,235,0.85)');
    grd.addColorStop(0.55, 'rgba(140,120,180,0.45)');
    grd.addColorStop(1, 'rgba(110,90,150,0)');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, p.w * 0.55, p.h, 0, 0, TAU);
    ctx.fill();
    ctx.restore();
    return true;
  },

  fighting: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    const t = p.life / p.ttl;
    if (p.kind === 'ring') {
      p.r += p.growth;
      const alpha = Math.max(0, 1 - t);
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      ctx.shadowColor = 'rgba(255,140,80,0.9)';
      ctx.shadowBlur = 10;
      ctx.strokeStyle = `rgba(255,190,140,${0.9 * alpha})`;
      ctx.lineWidth = 2.6;
      ctx.beginPath(); ctx.arc(p.cx, p.cy, p.r, 0, TAU); ctx.stroke();
      ctx.strokeStyle = `rgba(255,120,60,${0.5 * alpha})`;
      ctx.lineWidth = 5;
      ctx.stroke();
      ctx.restore();
      return true;
    }
    // spark
    p.x += Math.cos(p.angle) * p.speed;
    p.y += Math.sin(p.angle) * p.speed;
    p.speed *= 0.985;
    const alpha = t < 0.15 ? t / 0.15 : Math.max(0, 1 - (t - 0.15) / 0.85);
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = 'rgba(255,140,80,1)';
    ctx.shadowBlur = 10;
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 3.5);
    grd.addColorStop(0, `rgba(255,235,200,${alpha})`);
    grd.addColorStop(0.5, `rgba(255,120,60,${0.9 * alpha})`);
    grd.addColorStop(1, 'rgba(200,40,20,0)');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3.5, 0, TAU); ctx.fill();
    // streak
    ctx.strokeStyle = `rgba(255,200,140,${0.7 * alpha})`;
    ctx.lineWidth = 1.4;
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
    ctx.lineTo(p.x - Math.cos(p.angle) * 8, p.y - Math.sin(p.angle) * 8);
    ctx.stroke();
    ctx.restore();
    return true;
  },

  dragon: (p, ctx, w, h) => {
    p.angle += p.speed;
    p.life++;
    const x = p.cx + Math.cos(p.angle) * p.radius;
    const y = p.cy + Math.sin(p.angle) * p.radius * 0.65;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = 'rgba(111,53,252,0.95)';
    ctx.shadowBlur = 14;
    const grd = ctx.createRadialGradient(x, y, 0, x, y, p.r * 3.5);
    grd.addColorStop(0, 'rgba(220,190,255,0.95)');
    grd.addColorStop(0.5, 'rgba(150,90,255,0.75)');
    grd.addColorStop(1, 'rgba(111,53,252,0)');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(x, y, p.r * 3.5, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },

  dark: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    p.x += p.vx; p.y += p.vy;
    if (p.x - p.w * 0.5 > w + 20 || p.x + p.w * 0.5 < -20) return false;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.8, p.life / 60) * Math.max(0, 1 - (t - 0.8) / 0.2);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.filter = 'blur(4px)';
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.w * 0.55);
    grd.addColorStop(0, 'rgba(60,45,55,0.85)');
    grd.addColorStop(1, 'rgba(20,15,20,0)');
    ctx.fillStyle = grd;
    ctx.beginPath();
    ctx.ellipse(p.x, p.y, p.w * 0.5, p.h, 0, 0, TAU);
    ctx.fill();
    ctx.restore();
    return true;
  },

  steel: (p, ctx, w, h) => {
    p.life++;
    if (p.x > w + p.width) return false;
    p.x += p.vx;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const grd = ctx.createLinearGradient(p.x, 0, p.x + p.width, h);
    grd.addColorStop(0, 'rgba(255,255,255,0)');
    grd.addColorStop(0.5, 'rgba(255,255,255,0.55)');
    grd.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = grd;
    ctx.translate(p.x + p.width / 2, h / 2);
    ctx.rotate(-0.5);
    ctx.fillRect(-p.width, -h, p.width * 2, h * 2);
    ctx.restore();
    return true;
  },

  fairy: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    p.phase += 0.16;
    const t = p.life / p.ttl;
    const alpha = Math.sin(t * Math.PI) * 0.95;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = 'rgba(255,200,225,1)';
    ctx.shadowBlur = 12;
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
    grd.addColorStop(0, `rgba(255,255,255,${alpha})`);
    grd.addColorStop(0.5, `rgba(255,215,240,${0.8 * alpha})`);
    grd.addColorStop(1, 'rgba(214,133,173,0)');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 4, 0, TAU); ctx.fill();
    // cross-star
    ctx.strokeStyle = `rgba(255,255,255,${alpha})`;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(p.x - p.r * 2.5, p.y); ctx.lineTo(p.x + p.r * 2.5, p.y);
    ctx.moveTo(p.x, p.y - p.r * 2.5); ctx.lineTo(p.x, p.y + p.r * 2.5);
    ctx.stroke();
    ctx.restore();
    return true;
  },

  ground: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl || p.x > w + 15) return false;
    p.phase += 0.05;
    p.x += p.vx;
    const y = p.y + Math.sin(p.phase) * p.vyOsc * 8;
    const t = p.life / p.ttl;
    const alpha = Math.min(0.95, p.life / 20) * Math.max(0, 1 - (t - 0.75) / 0.25);
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = '#e0b96b';
    ctx.shadowColor = 'rgba(200,150,60,0.7)';
    ctx.shadowBlur = 4;
    ctx.beginPath(); ctx.arc(p.x, y, p.r, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },

  bug: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    p.twinkle += 0.22;
    p.x += p.vx + Math.sin(p.life * 0.1) * 0.35;
    p.y += p.vy + Math.cos(p.life * 0.13) * 0.35;
    if (p.x < 0 || p.x > w) p.vx *= -1;
    if (p.y < 0 || p.y > h) p.vy *= -1;
    const alpha = (Math.sin(p.twinkle) * 0.5 + 0.5) * 0.95;
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    ctx.shadowColor = 'rgba(220,240,110,1)';
    ctx.shadowBlur = 10;
    const grd = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
    grd.addColorStop(0, `rgba(250,255,160,${alpha})`);
    grd.addColorStop(0.5, `rgba(180,200,40,${0.75 * alpha})`);
    grd.addColorStop(1, 'rgba(120,140,20,0)');
    ctx.fillStyle = grd;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 4, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },

  normal: (p, ctx, w, h) => {
    p.life++;
    if (p.life > p.ttl) return false;
    p.phase += 0.12;
    const t = p.life / p.ttl;
    const alpha = Math.sin(t * Math.PI) * 0.75;
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = 'rgba(235,230,200,0.95)';
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, TAU); ctx.fill();
    ctx.restore();
    return true;
  },
};

// ─── canvas half (single type region) ────────────────────────────────
const HalfCanvas = ({ type, clipPath }) => {
  const ref = useRef(null);
  const rafRef = useRef(0);
  const particlesRef = useRef([]);
  const sizeRef = useRef({ w: 0, h: 0, dpr: 1 });

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || !FACTORIES[type]) return;

    const parent = canvas.parentElement;
    let running = true;

    const resize = () => {
      const rect = parent.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;
      sizeRef.current = { w: rect.width, h: rect.height, dpr };
      canvas.width  = Math.max(1, Math.round(rect.width  * dpr));
      canvas.height = Math.max(1, Math.round(rect.height * dpr));
      canvas.style.width  = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      const ctx = canvas.getContext('2d');
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const factory = FACTORIES[type];
      const count = COUNT[type] || 20;
      const arr = [];
      for (let i = 0; i < count; i++) arr.push(factory(i, rect.width, rect.height));
      particlesRef.current = arr;
    };

    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(parent);

    const ctx = canvas.getContext('2d');
    const factory = FACTORIES[type];
    const step = STEP[type];

    const respawn = (i) => {
      const { w, h } = sizeRef.current;
      particlesRef.current[i] = factory(i, w, h);
    };

    const loop = () => {
      if (!running) return;
      const { w, h } = sizeRef.current;
      ctx.clearRect(0, 0, w, h);
      drawTint(ctx, w, h, type);
      if (type === 'rock') drawRockTexture(ctx, w, h);
      const parts = particlesRef.current;
      for (let i = 0; i < parts.length; i++) {
        const alive = step(parts[i], ctx, w, h);
        if (!alive) respawn(i);
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);

    return () => {
      running = false;
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
    };
  }, [type]);

  return (
    <canvas
      ref={ref}
      className="absolute inset-0 pointer-events-none"
      style={clipPath ? { clipPath } : undefined}
      aria-hidden="true"
      data-testid={`type-canvas-${type}`}
    />
  );
};

const PokemonTypeCanvas = ({ types }) => {
  const list = useMemo(
    () => (Array.isArray(types) ? types.filter((t) => t && FACTORIES[t]).slice(0, 2) : []),
    [types]
  );
  if (list.length === 0) return null;
  if (list.length === 1) {
    return <HalfCanvas type={list[0]} />;
  }
  return (
    <>
      <HalfCanvas type={list[0]} clipPath="polygon(0 0, 100% 0, 0 100%)" />
      <HalfCanvas type={list[1]} clipPath="polygon(100% 0, 100% 100%, 0 100%)" />
    </>
  );
};

export default PokemonTypeCanvas;
